import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HeroCanvas from "@/components/HeroCanvas";
import TickerTape from "@/components/TickerTape";
import MeshBackground from "@/components/MeshBackground";
import HowItWorks from "@/components/landing/HowItWorks";
import SecuritySection from "@/components/landing/SecuritySection";
import FaqSection from "@/components/landing/FaqSection";
import AcademySection from "@/components/landing/AcademySection";
import NewsSection from "@/components/landing/NewsSection";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useLang } from "@/i18n";
import { usePrices } from "@/lib/usePrices";
import { fmtPrice } from "@/lib/api";
import { Reveal, CountUp } from "@/components/Reveal";
import {
  ShieldCheck, Landmark, LineChart, Vault, ArrowRight,
  TrendingUp, TrendingDown, CheckCircle2, Activity,
} from "lucide-react";

const SOLUTION_TABS = ["dca", "yield", "arb", "wealth"];
const SOLUTION_ICONS = { dca: LineChart, yield: Vault, arb: Activity, wealth: Landmark };
const SYMBOL_ORDER = ["BTCUSDT","ETHUSDT","BNBUSDT","SOLUSDT","XRPUSDT","DOGEUSDT","ADAUSDT","TRXUSDT","LINKUSDT","LTCUSDT"];

function TrustBar() {
  const { t } = useLang();
  const stats = [
    { value: "$2.4B", label: t("trust.aum") },
    { value: "99.99%", label: t("trust.uptime") },
    { value: "98%", label: t("trust.cold") },
    { value: "12,400+", label: t("trust.clients") },
  ];
  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 relative z-10">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <Reveal key={s.label} delay={i * 90}>
            <div
              className="group bg-white border border-[#E6EBF1] rounded-2xl p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-[#635BFF]/40 hover:shadow-[0_12px_32px_rgba(99,91,255,0.12)]"
            >
              <div className="font-display text-2xl lg:text-3xl font-bold text-[#0A2540] group-hover:text-[#635BFF] transition-colors">
                <CountUp value={s.value} />
              </div>
              <div className="text-xs text-[#425466] mt-1">{s.label}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

function Solutions() {
  const { t } = useLang();
  return (
    <section id="solutions" className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="solutions-section">
      <Reveal>
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#635BFF]">{t("solutions.eyebrow")}</p>
      <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A2540] mt-3 max-w-2xl">
        {t("solutions.title")}
      </h2>
      <p className="text-[#425466] mt-4 max-w-2xl text-base">{t("solutions.subtitle")}</p>
      </Reveal>

      <Tabs defaultValue="dca" className="mt-10">
        <TabsList className="bg-[#F6F9FC] border border-[#E6EBF1] h-auto flex-wrap justify-start gap-1 p-1 rounded-xl">
          {SOLUTION_TABS.map((key) => {
            const Icon = SOLUTION_ICONS[key];
            return (
              <TabsTrigger
                key={key}
                value={key}
                data-testid={`solution-tab-${key}`}
                className="data-[state=active]:bg-[#635BFF] data-[state=active]:text-white text-[#425466] gap-2 px-4 py-2.5 rounded-lg"
              >
                <Icon size={15} />
                {t(`solutions.${key}.title`)}
              </TabsTrigger>
            );
          })}
        </TabsList>
        {SOLUTION_TABS.map((key) => (
          <TabsContent key={key} value={key}>
            <div className="bg-white border border-[#E6EBF1] rounded-2xl p-8 lg:p-10 mt-4 grid lg:grid-cols-2 gap-8 shadow-[0_8px_30px_rgba(10,37,64,0.05)]">
              <div>
                <h3 className="font-display text-xl sm:text-2xl font-semibold text-[#0A2540]">
                  {t(`solutions.${key}.title`)}
                </h3>
                <p className="text-[#425466] mt-3 leading-relaxed">{t(`solutions.${key}.desc`)}</p>
              </div>
              <ul className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#425466]">
                    <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                    {t(`solutions.${key}.b${i}`)}
                  </li>
                ))}
              </ul>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </section>
  );
}

function MathSection() {
  const { t } = useLang();
  const rows = [
    { m: 1, price: 60000 }, { m: 2, price: 45000 },
    { m: 3, price: 52000 }, { m: 4, price: 68000 },
  ];
  const totalUnits = rows.reduce((s, r) => s + 1000 / r.price, 0);
  const avg = (4000 / totalUnits).toFixed(0);
  return (
    <section className="py-24 bg-white" data-testid="math-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#635BFF]">{t("math.eyebrow")}</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A2540] mt-3">
            {t("math.title")}
          </h2>
          <div className="bg-[#F6F9FC] border border-[#E6EBF1] rounded-2xl p-6 mt-8">
            <p className="text-xs uppercase tracking-widest text-slate-500">{t("math.formulaLabel")}</p>
            <p className="font-ticker text-2xl text-[#635BFF] mt-2">{t("math.formula")}</p>
            <p className="text-sm text-[#425466] mt-4 leading-relaxed">{t("math.formulaHint")}</p>
          </div>
        </div>
        </Reveal>
        <Reveal delay={120}>
        <div className="bg-white border border-[#E6EBF1] rounded-2xl p-6 lg:p-8 shadow-[0_16px_40px_rgba(10,37,64,0.08)]">
          <p className="text-sm font-medium text-[#0A2540] mb-4">{t("math.exampleTitle")}</p>
          <table className="w-full text-sm font-ticker">
            <thead>
              <tr className="text-slate-500 text-xs uppercase tracking-wider">
                <th className="text-left py-2">{t("math.colMonth")}</th>
                <th className="text-right py-2">{t("math.colPrice")}</th>
                <th className="text-right py-2">{t("math.colUnits")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.m} className="border-t border-[#E6EBF1] text-[#425466]">
                  <td className="py-2.5">#{r.m}</td>
                  <td className="text-right">${r.price.toLocaleString("en-US")}</td>
                  <td className="text-right text-emerald-600">{(1000 / r.price).toFixed(5)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-between items-center mt-5 pt-4 border-t border-[#635BFF]/20">
            <div>
              <p className="text-xs text-slate-500">{t("math.avg")}</p>
              <p className="font-ticker text-xl text-emerald-600 font-semibold">${Number(avg).toLocaleString("en-US")}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">{t("math.last")}</p>
              <p className="font-ticker text-xl text-[#0A2540] font-semibold">$68,000</p>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-4 leading-relaxed">{t("math.result")}</p>
        </div>
        </Reveal>
      </div>
    </section>
  );
}

function Markets() {
  const { t } = useLang();
  const { prices, live } = usePrices();
  const items = Object.values(prices).sort(
    (a, b) => SYMBOL_ORDER.indexOf(a.symbol) - SYMBOL_ORDER.indexOf(b.symbol)
  );
  return (
    <section id="markets" className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="markets-section">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <Reveal>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#635BFF]">{t("markets.eyebrow")}</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A2540] mt-3">
            {t("markets.title")}
          </h2>
          <p className="text-[#425466] mt-3">{t("markets.subtitle")}</p>
        </div>
        </Reveal>
        <div className="flex items-center gap-2 text-xs font-ticker text-emerald-600">
          <span className={`w-2 h-2 rounded-full ${live ? "bg-emerald-500 animate-pulse-dot" : "bg-amber-400"}`} />
          {live ? "LIVE · BINANCE" : "POLLING · BINANCE"}
        </div>
      </div>
      <div className="bg-white border border-[#E6EBF1] rounded-2xl mt-10 overflow-hidden overflow-x-auto shadow-[0_8px_30px_rgba(10,37,64,0.05)]">
        <table className="w-full text-sm min-w-[720px]" data-testid="markets-table">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-[#E6EBF1] bg-[#F6F9FC]">
              <th className="text-left px-6 py-4">{t("markets.asset")}</th>
              <th className="text-right px-6 py-4">{t("markets.price")}</th>
              <th className="text-right px-6 py-4">{t("markets.change")}</th>
              <th className="text-right px-6 py-4">{t("markets.high")}</th>
              <th className="text-right px-6 py-4">{t("markets.low")}</th>
              <th className="text-right px-6 py-4">{t("markets.volume")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((tk) => {
              const up = tk.changePct >= 0;
              return (
                <tr key={tk.symbol} className="border-b border-[#E6EBF1] hover:bg-[#F6F9FC] transition-colors">
                  <td className="px-6 py-4">
                    <span className="font-semibold text-[#0A2540]">{tk.name}</span>
                    <span className="text-slate-400 text-xs ml-1.5">/ USDT</span>
                  </td>
                  <td className="px-6 py-4 text-right font-ticker font-semibold text-[#0A2540]">${fmtPrice(tk.price)}</td>
                  <td className={`px-6 py-4 text-right font-ticker ${up ? "text-emerald-600" : "text-red-500"}`}>
                    <span className="inline-flex items-center gap-1 justify-end">
                      {up ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                      {up ? "+" : ""}{tk.changePct.toFixed(2)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right font-ticker text-slate-500">${fmtPrice(tk.high)}</td>
                  <td className="px-6 py-4 text-right font-ticker text-slate-500">${fmtPrice(tk.low)}</td>
                  <td className="px-6 py-4 text-right font-ticker text-slate-500">
                    ${(tk.volume / 1e6).toFixed(1)}M
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function About() {
  const { t } = useLang();
  const points = [
    { icon: ShieldCheck, title: t("about.point1"), desc: t("about.point1d") },
    { icon: Landmark, title: t("about.point2"), desc: t("about.point2d") },
    { icon: LineChart, title: t("about.point3"), desc: t("about.point3d") },
  ];
  return (
    <section id="about" className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="about-section">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#635BFF]">{t("about.eyebrow")}</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A2540] mt-3">
            {t("about.title")}
          </h2>
          <p className="text-[#425466] mt-5 leading-relaxed">{t("about.body")}</p>
          <div className="mt-8 space-y-5">
            {points.map((p) => (
              <div key={p.title} className="flex gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#635BFF]/10 border border-[#635BFF]/20 flex items-center justify-center shrink-0">
                  <p.icon size={18} className="text-[#635BFF]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#0A2540]">{p.title}</p>
                  <p className="text-sm text-[#425466] mt-0.5">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        </Reveal>
        <div className="relative">
          <img
            src="https://images.unsplash.com/photo-1487958449943-2429e8be8625?auto=format&fit=crop&w=900&q=80"
            alt="UCP headquarters"
            className="rounded-2xl border border-[#E6EBF1] shadow-2xl shadow-[#0A2540]/15 w-full object-cover aspect-[4/3]"
            loading="lazy"
          />
          <img
            src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=600&q=80"
            alt="Trading analytics"
            className="absolute -bottom-8 -left-8 w-56 rounded-xl border border-[#E6EBF1] shadow-2xl shadow-[#0A2540]/20 hidden sm:block animate-float-slow"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const { t } = useLang();
  const { prices } = usePrices();
  const btc = prices["BTCUSDT"];
  const eth = prices["ETHUSDT"];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Navbar />

      {/* Hero — Stripe-style animated gradient on white, diagonally clipped */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0"
          style={{ clipPath: "polygon(0 0, 100% 0, 100% 74%, 0 100%)" }}
        >
          <HeroCanvas />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/75 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-36 pb-52">
          <div className="max-w-3xl">
            <div className="animate-fade-up inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-[#E6EBF1] rounded-full px-4 py-1.5 text-xs font-semibold text-[#635BFF] shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" />
              {t("hero.badge")}
            </div>
            <h1 className="animate-fade-up delay-1 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#0A2540] mt-6 leading-[1.08]">
              {t("hero.titleA")}{" "}
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: "linear-gradient(100deg, #635BFF 0%, #FF5996 55%, #FF9966 100%)" }}
              >
                {t("hero.titleB")}
              </span>
            </h1>
            <p className="animate-fade-up delay-2 text-[#425466] text-base lg:text-lg mt-6 leading-relaxed max-w-2xl">
              {t("hero.subtitle")}
            </p>
            <div className="animate-fade-up delay-3 flex flex-wrap items-center gap-4 mt-9">
              <Link to="/register">
                <Button
                  data-testid="hero-cta-primary"
                  className="bg-[#635BFF] hover:bg-[#0A2540] text-white px-7 h-12 text-sm font-semibold shadow-xl shadow-[#635BFF]/30 rounded-full transition-colors"
                >
                  {t("hero.ctaPrimary")} <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <a
                href="#solutions"
                data-testid="hero-cta-secondary"
                className="group inline-flex items-center gap-1.5 text-sm font-semibold text-[#635BFF] hover:text-[#0A2540] transition-colors px-2 h-12"
              >
                {t("hero.ctaSecondary")}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </div>

          {/* Floating live price card */}
          <div className="animate-fade-up delay-4 hidden lg:block absolute right-8 top-32 animate-float-slow">
            <div className="rounded-2xl p-[1.5px] bg-gradient-to-r from-[#635BFF] via-[#FF5996] to-[#00D4FF] animate-border-flow shadow-2xl shadow-[#635BFF]/25">
            <div className="bg-white/90 backdrop-blur-xl rounded-[14px] p-5 w-72" data-testid="hero-live-card">
              <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" />
                {t("hero.liveLabel")}
              </p>
              {[btc, eth].filter(Boolean).map((tk) => (
                <div key={tk.symbol} className="flex items-center justify-between mt-4">
                  <span className="text-sm font-semibold text-[#0A2540]">{tk.name}<span className="text-slate-400 text-xs">/USDT</span></span>
                  <div className="text-right">
                    <p className="font-ticker text-sm text-[#0A2540]">${fmtPrice(tk.price)}</p>
                    <p className={`font-ticker text-xs ${tk.changePct >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                      {tk.changePct >= 0 ? "+" : ""}{tk.changePct.toFixed(2)}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
            </div>
          </div>
        </div>
      </section>

      <TickerTape />

      <div className="pt-16"><TrustBar /></div>
      <Solutions />
      <HowItWorks />
      <MathSection />
      <Markets />
      <AcademySection />
      <SecuritySection />
      <About />
      <FaqSection />
      <NewsSection />

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24" data-testid="cta-section">
        <div className="relative bg-[#0A2540] rounded-3xl p-10 lg:p-16 overflow-hidden text-center">
          <MeshBackground className="absolute inset-0" opacity={0.35} beams />
          <h2 className="relative font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
            {t("cta.title")}
          </h2>
          <p className="relative text-slate-300 mt-4 max-w-xl mx-auto">{t("cta.subtitle")}</p>
          <Link to="/register">
            <Button
              data-testid="cta-register-btn"
              className="relative bg-[#635BFF] hover:bg-white hover:text-[#0A2540] text-white px-8 h-12 mt-8 rounded-full font-semibold shadow-xl shadow-[#635BFF]/40 transition-colors"
            >
              {t("cta.button")} <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
